import Image from "next/image";
import Link from "next/link";
import useDateLocale from "hooks/useDateLocale";
import Layout from "@components/layout/layout";
import SideBar from "@components/sidebar/sidebar";
import VideosTrendingNow from "@components/core/videos-trending-now";
import VideoActionButtons from "./video-action-buttons";
import StickyBox from "react-sticky-box";
import VideoQrContainer from "./video-qr-container";
import { VideoPageQuery } from "@graphql/generated/graphql";
import { useState } from "react";
import { t, Trans } from "@lingui/macro";
import { formatDistanceToNowStrict } from "date-fns";
import { DialogName, triggerDialog } from "@components/core/dialog";
import ErrorPage from "pages/404";
import { ApolloError } from "@apollo/client/errors";
import { useRouter } from "next/router";

interface IVideoSummaryPageProps {
  data: VideoPageQuery;
  loading: Boolean;
  error: ApolloError;
}

const VideoSummaryPage: React.FunctionComponent<IVideoSummaryPageProps> = ({
  data,
  loading,
  error,
}) => {
  const router = useRouter();

  if (!loading && data && !data.video) {
    router.push("/videos?contentNotFound=true");
    return <></>;
  }

  return (
    <Layout
      showNewsStacks={true}
      showCTA={false}
      showFooter={false}
      className="!overflow-visible"
      retainWholePage={true}
    >
      <div className="mx-auto my-6 flex max-w-7xl flex-row items-start px-4 2xs:px-6 lg:space-x-20">
        {!loading && !error && data ? (
          <div>
            <VideoSummary video={data.video} />

            <div className="mt-4 h-[1px] w-full bg-off-white lg:mt-14"></div>

            <VideoQrContainer />

            <div className="mt-4 h-[1px] w-full bg-off-white lg:mt-14"></div>

            {data.video?.id && (
              <div className="mt-8">
                <VideosTrendingNow currentVideoId={data.video.id} />
              </div>
            )}
          </div>
        ) : (loading || !data) && !error ? (
          <p className="flex-1 py-4 text-center font-sen text-text-lightest">
            <Trans>Loading...</Trans>
          </p>
        ) : (
          <ErrorPage />
        )}
        <StickyBox offsetTop={20} offsetBottom={20}>
          <SideBar />
        </StickyBox>
      </div>
    </Layout>
  );
};

interface VideoSummaryProps {
  video: VideoPageQuery["video"];
}

const VideoSummary: React.FunctionComponent<VideoSummaryProps> = ({
  video,
}) => {
  const {
    description,
    title,
    pubDate,
    newsPublisher,
    externalId,
    isWebViewable,
  } = video;

  const locale = useDateLocale();

  const openViewableVideoDialog = (event) => {
    event.preventDefault();
    triggerDialog(DialogName.viewableVideo);
  };

  return (
    <div key={externalId}>
      <div
        className="youtube-container"
        onClick={(event) => {
          if (!isWebViewable) {
            openViewableVideoDialog(event);
          }
        }}
      >
        <iframe
          className={`w-full ${!isWebViewable && "pointer-events-none"}`}
          src={`https://www.youtube.com/embed/${externalId}`}
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        ></iframe>
        {/* Trick user to click the video if video isnt viewable */}
        {!isWebViewable && (
          <div className="youtube-container absolute -top-[30px] w-full cursor-pointer"></div>
        )}
      </div>

      <div className="mt-6 flex items-center justify-between">
        <div className="relative h-[30px] w-[53px] drop-shadow-custom">
          <Link href={`/publishers/${newsPublisher.slug}?view=videos`}>
            <a>
              <Image
                className="rounded-lg"
                src={newsPublisher.imageUrl}
                alt={newsPublisher.name}
                layout="fill"
                objectFit="cover"
                priority
              />
            </a>
          </Link>
        </div>

        <div className="ml-4 flex-1">
          <Link href={`/publishers/${newsPublisher.slug}?view=videos`}>
            <a>
              <span className="font-poppins text-sm font-bold">
                {newsPublisher.name}
              </span>
            </a>
          </Link>
        </div>
      </div>

      <h1 className="mt-6 font-poppins text-2xl font-bold text-text-darkest">
        {title}
      </h1>
      <p className="mt-2 font-sen text-xs text-text-medium">
        {formatDistanceToNowStrict(new Date(pubDate), {
          locale,
          addSuffix: true,
        })}
      </p>

      <ExpandableDescription description={description} />

      <div className="mt-2 flex items-center justify-end">
        <VideoActionButtons video={video} />
      </div>
    </div>
  );
};

interface ExpandableDescriptionProps {
  description: String;
}

const ExpandableDescription: React.FunctionComponent<
  ExpandableDescriptionProps
> = ({ description }) => {
  const [showMore, setShowMore] = useState(false);
  const showMoreText = t`Show more`;
  const showLessText = t`Show less`;

  if (description.length <= 500) {
    return (
      <div
        className="mt-2 whitespace-pre-wrap font-poppins text-text-dark"
        style={{ wordBreak: "break-word" }}
      >
        {description}
      </div>
    );
  }

  return (
    <div>
      <div
        className="mt-2 whitespace-pre-wrap font-poppins text-text-dark"
        style={{ wordBreak: "break-word" }}
      >
        {showMore ? description : `${description.substring(0, 500)}...`}
      </div>
      <button
        className="mt-4 font-poppins text-primary2 underline"
        onClick={() => {
          setShowMore(!showMore);
        }}
      >
        {showMore ? showLessText : showMoreText}
      </button>
    </div>
  );
};
export default VideoSummaryPage;
